import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    Renderer2
} from "@angular/core";
import {FUIAbstractInput} from "../../framework/abstract-input";

@Directive()
export abstract class FUIAbstractImageInput extends FUIAbstractInput implements OnInit, OnDestroy {
    private static HEADER_LEN: number = 6;
    private static LOGICAL_SCREEN_DESC_LEN: number = 7;

    @Input() maxImageSize: number;
    @Input() allowSvg: boolean = false;
    @Input() showImage: boolean = true;
    @Input() showOpacitySlider: boolean = false;
    @Input() placeholderImage: string;
    @Output() imageOpacityChange: EventEmitter<number> = new EventEmitter<number>();

    @HostBinding("class.dragging") public dragging: boolean;

    private dragEnterCount: number = 0;
    private image: HTMLImageElement = new Image();
    private canvas: HTMLCanvasElement = document.createElement("canvas");

    private teardownEventListenerFn: Function;

    constructor(changeDetectorRef: ChangeDetectorRef,
                protected element: ElementRef,
                protected ngZone: NgZone,
                protected renderer: Renderer2) {
        super(changeDetectorRef);
    }

    private _imageOpacity: number = 1.0;

    get imageOpacity(): number {
        return this._imageOpacity;
    }

    @Input()
    set imageOpacity(imageOpacity: number) {
        if (!this.valueEqual(imageOpacity, this._imageOpacity)) {
            this._imageOpacity = imageOpacity;
            this.imageOpacityChange.emit(imageOpacity);
            if (imageOpacity == undefined) {
                setTimeout(() => {
                    this.imageOpacity = 1.0;
                    this.changeDetectorRef.detectChanges();
                });
            }
        }
    }

    get validFileTypes(): string {
        return ".gif,.jpg,.jpeg,.png" + (this.allowSvg ? ",.svg" : "");
    }

    get opacityPercent(): number {
        return this._imageOpacity == undefined ? 100 : Math.round(this._imageOpacity * 100);
    }

    ngOnInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.teardownEventListenerFn = this.renderer.listen(this.element.nativeElement,
                "dragover",
                (dragEvent: DragEvent) => {
                    dragEvent.preventDefault();
                    dragEvent.stopPropagation();
                    dragEvent.dataTransfer.dropEffect = "copy";
                });
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.teardownEventListenerFn();
    }

    public handleDragEnter(dragEvent: DragEvent): void {
        if (this.showImage) {
            this.dragEnterCount++;
            this.dragging = !!this.dragEnterCount;
        }
    }

    public handleDragLeave(dragEvent: DragEvent): void {
        if (this.showImage) {
            this.dragEnterCount--;
            this.dragging = !!this.dragEnterCount;
        }
    }

    public fileDropped(dragEvent: DragEvent): void {
        if (this.showImage) {
            dragEvent.preventDefault();
            dragEvent.stopPropagation();
            this.dragEnterCount = 0;
            this.dragging = false;
            let fileList: FileList = dragEvent.dataTransfer.files;
            if (fileList && fileList.length > 0) {
                this.loadImageFromFile(fileList[0]);
            }
        }
    }

    public fileSelected(event: Event): void {
        let fileList: FileList = (<HTMLInputElement>event.target).files;
        if (fileList && fileList.length > 0) {
            this.loadImageFromFile(fileList[0]);
        }
        (<HTMLInputElement>event.target).value = null;
    }

    override click(event?: MouseEvent): void {
        super.click();
        if (event) {
            event.stopPropagation();
        }
    }

    /**
     * If a user presses enter or space while focused on the element,
     * use the click method to apply the relevant interaction
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keypress", ["$event"])
    public keypress(keyboardEvent: KeyboardEvent) {
        if (!this.disabled) {
            if (keyboardEvent.code === "Enter" || keyboardEvent.code === "Space" || keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                this.click();
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    public clear(event: Event) {
        this.value = null;
        event.stopPropagation();
    }

    private loadImageFromFile(file: File, allowGif: boolean = false) {
        if (!file.type.startsWith("image/")) {
            return;
        }
        if (file.type.startsWith("image/svg") && !this.allowSvg) {
            return;
        }

        let reader: FileReader = new FileReader();
        if (this.maxImageSize && file.size > this.maxImageSize) {
            if (file.type === "image/gif" && !allowGif) {
                reader.onload = (readerEvent: Event) => {
                    if (!this.isAnimatedGif((<FileReader>readerEvent.target).result)) {
                        this.loadImageFromFile(file, true);
                    }
                };
                reader.readAsArrayBuffer(file);
            } else {
                reader.onload = (readerEvent: Event) => {
                    this.resizeImage(<string>(<FileReader>readerEvent.target).result, this.maxImageSize, file.type)
                        .then((dataUrl: string) => {
                            this.value = this.getBase64(dataUrl);
                        });
                };
                reader.readAsDataURL(file);
            }
        } else if (file.type.startsWith("image/svg")) {
            reader.onload = (readerEvent: Event) => {
                let text: string = <string>(<FileReader>readerEvent.target).result;
                this.value = this.getSvgInBase64(text);
            };
            reader.readAsText(file);
        } else {
            reader.onload = (readerEvent: Event) => {
                let dataUrl: string = <string>(<FileReader>readerEvent.target).result;
                this.value = this.getBase64(dataUrl);
            };
            reader.readAsDataURL(file);
        }
    }

    private resizeImage(dataUrl: string, requiredSize: number, fileType: string): Promise<string> {
        return new Promise((resolve, reject) => {
            let currentSize: number = this.getBinarySize(this.getBase64(dataUrl));
            if (currentSize <= this.maxImageSize) {
                resolve(dataUrl);
                return;
            }
            let factor: number = Math.sqrt(currentSize / requiredSize);
            this.image.onload = () => {
                let width: number = this.image.width / factor;
                let height: number = this.image.height / factor;
                this.canvas.width = width;
                this.canvas.height = height;
                let context: CanvasRenderingContext2D = this.canvas.getContext("2d");
                context.clearRect(0, 0, width, height);
                context.drawImage(this.image, 0, 0, width, height);
                dataUrl = this.canvas.toDataURL(fileType);
                if (this.getBinarySize(this.getBase64(dataUrl)) > requiredSize) {
                    this.resizeImage(dataUrl, requiredSize, fileType)
                        .then((dataUrl: string) => {
                            resolve(dataUrl);
                        });
                } else {
                    resolve(dataUrl);
                }
            };
            this.image.src = dataUrl;
        })
    }

    private getBase64(dataUrl: string): string {
        return dataUrl.substring(dataUrl.indexOf(",") + 1);
    }

    private getSvgInBase64(text: string): string {
        if (text.match(/^<svg\b/)) {
            return btoa(text);
        }
        if (text.match(/^<\?xml\b/)) {
            const i: number = text.search(/<svg\b/);
            return i == -1 ? undefined : btoa(text.slice(i));
        }
        return undefined;
    }

    private getBinarySize(base64: string): number {
        return base64.length / 4 * 3;
    }

    private isAnimatedGif(buffer: any): boolean {
        // Start from last 4 bytes of the Logical Screen Descriptor
        let dv: DataView = new DataView(buffer, FUIAbstractImageInput.HEADER_LEN + FUIAbstractImageInput.LOGICAL_SCREEN_DESC_LEN - 3);
        let globalColorTable: number = dv.getUint8(0);	// aka packet byte
        let globalColorTableSize: number = 0;

        // check first bit, if 0, then we don't have a Global Color Table
        if (globalColorTable & 0x80) {
            // grab the last 3 bits, to calculate the global color table size -> RGB * 2^(N+1)
            // N is the value in the last 3 bits.
            globalColorTableSize = 3 * Math.pow(2, (globalColorTable & 0x7) + 1);
        }

        // move on to the Graphics Control Extension
        let offset: number = 3 + globalColorTableSize;
        let extensionIntroducer: number = dv.getUint8(offset);
        let graphicsControlLabel: number = dv.getUint8(offset + 1);
        let delayTime: number = 0;

        // Graphics Control Extension section is where GIF animation data is stored
        // First 2 bytes must be 0x21 and 0xF9
        if ((extensionIntroducer & 0x21) === 0x21 && (graphicsControlLabel & 0xF9) == 0xF9) {
            // skip to the 2 bytes with the delay time
            delayTime = dv.getUint16(offset + 4);
        }

        return !!delayTime;
    }
}
